import { EventBus, EVENTS } from '../../../shared/EventBus';
import { BOSS_MOOD } from '../../../config/atmosphere/bossMood.config';
import { BossMoodModel } from './BossMoodModel';
import type { BossPhaseChangeEvent } from '../../../shared/types/NetworkMessages';
import type { BossPhaseResetEvent, NpcDefeatedEvent } from '../../../shared/types/NetworkMessages';

/**
 * BossMoodDriver — a reusable, fully-decoupled bridge that makes the scene
 * atmosphere swell during a boss fight:
 *
 *   EVENTS.BOSS_PHASE_CHANGED  (from the server `bossPhaseChange` broadcast →
 *     NetworkManager → EventBus)  →  phase-index accumulator (BossMoodModel)
 *   →  EVENTS.BOSS_MOOD  →  AtmosphereLayer multiplies it onto the per-level
 *     mood alongside sceneMood (time-of-day) and combatMood (cast-heat).
 *
 *   final = sceneMood × combatMood × bossMood
 *
 * On EVENTS.NPC_DEFEATED the tracked boss is cleared and bossMood resets to
 * neutral (1) so the atmosphere settles back after the fight. Client-
 * presentation only — zero server/sim impact, MP-safe. Orthogonal to
 * CombatMoodDriver (separate multiplier, separate event).
 *
 * Lane E S1 — the Hollow Coil undercroft dais swell.
 */
class BossMoodDriver {
  private model = new BossMoodModel();
  private unsubPhase: (() => void) | null = null;
  private unsubDefeat: (() => void) | null = null;
  private unsubReset: (() => void) | null = null;
  private lastEmitted = 1;
  private started = false;

  /** Idempotent. No-op when disabled in config. */
  start(): void {
    if (this.started || !BOSS_MOOD.enabled) return;
    this.started = true;
    const bus = EventBus.getInstance();
    this.unsubPhase = bus.on(EVENTS.BOSS_PHASE_CHANGED, (msg: BossPhaseChangeEvent) => {
      if (!msg?.npcId) return;
      const next = this.model.onPhaseChange(msg.npcId);
      this.emit(next);
    });
    this.unsubDefeat = bus.on(EVENTS.NPC_DEFEATED, (msg: NpcDefeatedEvent) => {
      if (!msg?.npcId) return;
      // Only react if this npcId is a tracked boss — non-boss defeats are a
      // no-op (the model's onDefeat is safe regardless, but skipping the
      // recompute avoids needless emits on every trash mob kill).
      if (this.model.phaseCount(msg.npcId) === 0) return;
      const next = this.model.onDefeat(msg.npcId);
      this.emit(next);
    });
    // A boss that leashed home (plan 07) ends the fight like a defeat does.
    this.unsubReset = bus.on(EVENTS.BOSS_PHASE_RESET, (msg: BossPhaseResetEvent) => {
      if (!msg?.npcId || this.model.phaseCount(msg.npcId) === 0) return;
      this.emit(this.model.onDefeat(msg.npcId));
    });
  }

  private emit(next: number): void {
    if (Math.abs(next - this.lastEmitted) < 1e-6) return;
    this.lastEmitted = next;
    EventBus.getInstance().emit(EVENTS.BOSS_MOOD, { intensity: next });
  }

  stop(): void {
    this.unsubPhase?.();
    this.unsubPhase = null;
    this.unsubDefeat?.();
    this.unsubDefeat = null;
    this.unsubReset?.();
    this.unsubReset = null;
    this.model.reset();
    this.lastEmitted = 1;
    this.started = false;
  }
}

/** Process singleton — one boss-mood bridge for the client. */
export const bossMoodDriver = new BossMoodDriver();

/** Start the boss-mood bridge (called once at game bootstrap). Idempotent. */
export function startBossMoodDriver(): void {
  bossMoodDriver.start();
}
